import type { ItemTextDto } from "@/models/dto/questionDto";
import type {
  QuestionnaireItemSetDto,
  QuestionnaireTemplateCommonItemDto,
} from "@/models/dto/questionnaireItemSetDto";
import { QuestionnaireLanguage } from "@/models/enum/questionnaireLanguage";
import {
  getCommonItemIdForQuestionType,
  QuestionnaireTemplateCommonItemsId,
} from "@/models/enum/questionnaireTemplateCommonItemsId";
import type { QuestionType } from "@/models/enum/questionType";
import type { QuestionnaireTemplate } from "@/models/questionnaireTemplate";
import { BooleanOption } from "@/models/questions/booleanOption";
import ReceivedQuestionnaire from "@/models/receivedQuestionnaire";
import { getQuestionnaireForm } from "@/services/questionnaireService";
import { getTemplate } from "@/services/templateService";
import { defineStore } from "pinia";

export interface SelectedQuestionnaireState {
  questionnaire: ReceivedQuestionnaire;
  progress: 1 | 2 | 3;
  itemSets: QuestionnaireItemSetDto[];
  commonItems: QuestionnaireTemplateCommonItemDto[];
  language: QuestionnaireLanguage;
  reusingAnswers: boolean;
  questionnaires: ReceivedQuestionnaire[];
}

export const useSelectedQuestionnaire = defineStore("selectedQuestionnaire", {
  state: (): SelectedQuestionnaireState => {
    return {
      questionnaire: new ReceivedQuestionnaire(),
      progress: 1,
      itemSets: new Array<QuestionnaireItemSetDto>(),
      commonItems: new Array<QuestionnaireTemplateCommonItemDto>(),
      language: QuestionnaireLanguage.En,
      reusingAnswers: false,
      questionnaires: new Array<ReceivedQuestionnaire>(),
    };
  },

  actions: {
    setSelectedQuestionnaireParams(questionnaire: ReceivedQuestionnaire) {
      this.questionnaire = questionnaire;
      this.questionnaires = [questionnaire];
    },

    setSelectedQuestionnairesParams(questionnaires: ReceivedQuestionnaire[]) {
      this.questionnaire = questionnaires[0];
      this.questionnaires = questionnaires;
    },

    setReusingAnswers() {
      this.reusingAnswers = true;
    },

    async getSelectedQuestionnaire(
      selectContextId: string,
      language: QuestionnaireLanguage
    ): Promise<QuestionnaireItemSetDto[]> {
      const questionnaireForm = await getQuestionnaireForm(
        selectContextId,
        language
      );

      this.itemSets = questionnaireForm.itemSets;
      this.commonItems = questionnaireForm.commonItems;
      this.language = language;
      return questionnaireForm.itemSets;
    },

    async getSelectedQuestionnaireTemplate(
      templateId: string,
      language: QuestionnaireLanguage
    ): Promise<QuestionnaireTemplate> {
      const template = await getTemplate(templateId, language);
      this.commonItems = template.commonItems;
      return template;
    },

    getCommonItems(id: QuestionnaireTemplateCommonItemsId): ItemTextDto[] {
      return (
        this.commonItems.find(
          (item: QuestionnaireTemplateCommonItemDto) => item.id == id
        )?.entries ?? new Array<ItemTextDto>()
      );
    },

    getCommonItemsForQuestionType(questionType: QuestionType): ItemTextDto[] {
      const id = getCommonItemIdForQuestionType(questionType);
      return (
        this.commonItems.find(
          (item: QuestionnaireTemplateCommonItemDto) => item.id == id
        )?.entries ?? new Array<ItemTextDto>()
      );
    },

    getBooleansOptions(questionType: QuestionType): BooleanOption[] {
      const booleans = this.getCommonItemsForQuestionType(questionType);
      return booleans
        ? booleans.map((x: ItemTextDto) => new BooleanOption(x.id, x.text))
        : new Array<BooleanOption>();
    },

    getTextForAnswerId(answerId: string, questionType: QuestionType): string {
      const entries = this.getCommonItemsForQuestionType(questionType);
      const entry = entries.find((x) => x.id == answerId);
      return entry?.text ?? "";
    },

    getKeyPersonnelDisplayTextItems(): ItemTextDto[] {
      const person = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Person
      );
      const address = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Address
      );
      const keyPersonnel = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.KeyPersonnel
      );
      return [...person, ...address, ...keyPersonnel];
    },

    getBeneficialOwnerDisplayTextItems(): ItemTextDto[] {
      const person = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Person
      );
      const address = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Address
      );
      const keyPersonnel = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.KeyPersonnel
      );
      const beneficialOwners = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.BeneficialOwner
      );
      return [...person, ...address, ...keyPersonnel, ...beneficialOwners];
    },

    getIndividualOwnerDisplayTextItems(): ItemTextDto[] {
      const person = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.PersonV2
      );
      const pepRca = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.PepRcaPerson
      );
      const address = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Address
      );
      const sanctions = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Sanctions
      );
      return [...person, ...pepRca, ...address, ...sanctions];
    },

    getCompanyOwnerDisplayTextItems(): ItemTextDto[] {
      const company = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Company
      );
      const companyOwnership = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.OwnershipCompany
      );
      const address = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Address
      );
      const sanctions = this.getCommonItems(
        QuestionnaireTemplateCommonItemsId.Sanctions
      );
      return [...company, ...companyOwnership, ...address, ...sanctions];
    },
  },
});
